import { useEffect, useRef, type RefObject } from "react";
import type { Message } from "../../api/types.js";

export type ThreadScrollController = {
  threadRef: RefObject<HTMLDivElement | null>;
  askDockRef: RefObject<HTMLDivElement | null>;
  threadFollowPausedRef: RefObject<boolean>;
  lastThreadScrollTopRef: RefObject<number>;
  setRunningActive: (active: boolean) => void;
  threadScrollContainer: () => HTMLElement | null;
  isThreadNearBottom: (threshold?: number) => boolean;
  scrollThreadToBottom: (behavior?: ScrollBehavior) => void;
  resumeThreadFollow: () => void;
  scheduleThreadFollow: (options?: { force?: boolean; behavior?: ScrollBehavior }) => void;
};

export function useThreadScroll({
  currentSessionId,
  isWelcome,
  pendingAskMessage
}: {
  currentSessionId: string | undefined;
  isWelcome: boolean;
  pendingAskMessage: Message | null;
}): ThreadScrollController {
  const askDockSafeGap = 36;
  const threadRef = useRef<HTMLDivElement>(null);
  const askDockRef = useRef<HTMLDivElement>(null);
  const runningRef = useRef(false);
  const threadFollowPausedRef = useRef(false);
  const threadFollowFrameRef = useRef<number | null>(null);
  const threadScrollActiveTimerRef = useRef<number | null>(null);
  const programmaticThreadScrollUntilRef = useRef(0);
  const lastThreadScrollTopRef = useRef(0);

  function threadScrollCandidates() {
    const thread = threadRef.current;
    return [
      thread,
      document.querySelector<HTMLElement>(".app-shell"),
      document.querySelector<HTMLElement>(".workspace"),
      document.querySelector<HTMLElement>(".view")
    ].filter(Boolean) as HTMLElement[];
  }

  function threadScrollContainer() {
    const thread = threadRef.current;
    const candidates = threadScrollCandidates();
    return candidates.find((element) => {
      const overflowY = getComputedStyle(element).overflowY;
      return !["visible", "clip"].includes(overflowY) && element.scrollHeight - element.clientHeight > 2;
    }) || thread;
  }

  function isThreadNearBottom(threshold = 260) {
    const container = threadScrollContainer();
    if (!container) return true;
    const scrollRoom = container.scrollHeight - container.clientHeight;
    if (scrollRoom <= 2) return true;
    return scrollRoom - container.scrollTop <= threshold;
  }

  function scrollThreadToBottom(behavior: ScrollBehavior = "auto") {
    const container = threadScrollContainer();
    if (!container) return;
    programmaticThreadScrollUntilRef.current = performance.now() + 160;
    container.scrollTo({ top: container.scrollHeight, behavior });
    lastThreadScrollTopRef.current = container.scrollTop;
  }

  function setThreadScrollActive() {
    const container = threadScrollContainer();
    if (!container) return;
    container.classList.add("is-scroll-active");
    if (threadScrollActiveTimerRef.current) window.clearTimeout(threadScrollActiveTimerRef.current);
    threadScrollActiveTimerRef.current = window.setTimeout(() => {
      threadScrollCandidates().forEach((element) => element.classList.remove("is-scroll-active"));
      threadScrollActiveTimerRef.current = null;
    }, 850);
  }

  function pauseThreadFollow() {
    if (!runningRef.current) return;
    threadFollowPausedRef.current = true;
    if (threadFollowFrameRef.current) {
      window.cancelAnimationFrame(threadFollowFrameRef.current);
      threadFollowFrameRef.current = null;
    }
  }

  function resumeThreadFollow() {
    threadFollowPausedRef.current = false;
  }

  function scheduleThreadFollow(options: { force?: boolean; behavior?: ScrollBehavior } = {}) {
    const force = Boolean(options.force);
    if (!force && threadFollowPausedRef.current) return;
    if (threadFollowFrameRef.current) return;
    const followFrame = (remainingFrames: number) => {
      threadFollowFrameRef.current = window.requestAnimationFrame(() => {
        threadFollowFrameRef.current = null;
        if (!force && threadFollowPausedRef.current) return;
        scrollThreadToBottom(options.behavior || "auto");
        if (!runningRef.current || threadFollowPausedRef.current || remainingFrames <= 0) return;
        followFrame(remainingFrames - 1);
      });
    };
    followFrame(5);
  }

  function setRunningActive(active: boolean) {
    runningRef.current = active;
    if (active) return;
    threadFollowPausedRef.current = false;
    if (threadFollowFrameRef.current) {
      window.cancelAnimationFrame(threadFollowFrameRef.current);
      threadFollowFrameRef.current = null;
    }
  }

  useEffect(() => {
    const containers = threadScrollCandidates();
    const initialContainer = threadScrollContainer();
    lastThreadScrollTopRef.current = initialContainer?.scrollTop || 0;

    const isTextInputTarget = (target: EventTarget | null) => {
      const element = target instanceof HTMLElement ? target : null;
      return Boolean(element?.closest("input, textarea, select, [contenteditable='true']"));
    };

    const handleScrollIntent = (event: WheelEvent | TouchEvent | KeyboardEvent) => {
      if (!runningRef.current) return;
      if (event instanceof KeyboardEvent) {
        if (isTextInputTarget(event.target)) return;
        const scrollUpKey = ["ArrowUp", "PageUp", "Home"].includes(event.key) || (event.key === " " && event.shiftKey);
        const scrollDownKey = ["ArrowDown", "PageDown", "End"].includes(event.key) || (event.key === " " && !event.shiftKey);
        if (scrollUpKey) {
          setThreadScrollActive();
          pauseThreadFollow();
        }
        if (scrollDownKey) {
          setThreadScrollActive();
          if (isThreadNearBottom(160)) resumeThreadFollow();
        }
        return;
      }
      if (event instanceof WheelEvent && event.deltaY < 0) {
        setThreadScrollActive();
        pauseThreadFollow();
        return;
      }
      if (event instanceof WheelEvent && event.deltaY > 0) setThreadScrollActive();
      if (event.type === "touchstart") {
        setThreadScrollActive();
        pauseThreadFollow();
      }
    };

    const handleScroll = () => {
      const container = threadScrollContainer();
      if (!container) return;
      if (performance.now() > programmaticThreadScrollUntilRef.current) setThreadScrollActive();
      const nextTop = container.scrollTop;
      if (runningRef.current) {
        if (nextTop < lastThreadScrollTopRef.current - 2 && !isThreadNearBottom(60)) {
          pauseThreadFollow();
        }
        if (isThreadNearBottom(60)) resumeThreadFollow();
      }
      lastThreadScrollTopRef.current = nextTop;
    };

    containers.forEach((container) => container.addEventListener("scroll", handleScroll, { passive: true }));
    containers.forEach((container) => container.addEventListener("pointerdown", setThreadScrollActive, { passive: true }));
    window.addEventListener("wheel", handleScrollIntent, { passive: true });
    window.addEventListener("touchstart", handleScrollIntent, { passive: true });
    window.addEventListener("keydown", handleScrollIntent);

    return () => {
      containers.forEach((container) => container.removeEventListener("scroll", handleScroll));
      containers.forEach((container) => container.removeEventListener("pointerdown", setThreadScrollActive));
      window.removeEventListener("wheel", handleScrollIntent);
      window.removeEventListener("touchstart", handleScrollIntent);
      window.removeEventListener("keydown", handleScrollIntent);
      if (threadScrollActiveTimerRef.current) {
        window.clearTimeout(threadScrollActiveTimerRef.current);
        threadScrollActiveTimerRef.current = null;
      }
      containers.forEach((container) => container.classList.remove("is-scroll-active"));
    };
  }, [currentSessionId, isWelcome]);

  useEffect(() => {
    const root = document.documentElement;
    const appShell = document.querySelector(".app-shell");
    let frame = 0;
    let lastExtraSpace = 0;

    const setAskDockExtraSpace = (value: number) => {
      const pixels = Math.max(0, Math.ceil(Number(value) || 0));
      lastExtraSpace = pixels;
      root.style.setProperty("--ask-dock-extra-space", `${pixels}px`);
      appShell?.classList.toggle("ask-question-open", pixels > 0);
    };

    const isNearThreadBottom = () => {
      const container = threadScrollContainer();
      if (!container) return true;
      const scrollRoom = container.scrollHeight - container.clientHeight;
      if (scrollRoom <= 2) return true;
      return scrollRoom - container.scrollTop <= 260 + lastExtraSpace;
    };

    const scheduleLayoutUpdate = (options: { scroll: boolean }) => {
      if (frame) window.cancelAnimationFrame(frame);
      const shouldScroll = options.scroll && !threadFollowPausedRef.current && isNearThreadBottom();
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const card = askDockRef.current?.querySelector<HTMLElement>('[data-ask-user-question-card][data-ask-status="pending"]');
        if (!card || isWelcome) {
          setAskDockExtraSpace(0);
          return;
        }
        setAskDockExtraSpace(card.getBoundingClientRect().height + askDockSafeGap);
        if (shouldScroll) {
          window.requestAnimationFrame(() => {
            const container = threadScrollContainer();
            if (container) {
              programmaticThreadScrollUntilRef.current = performance.now() + 160;
              container.scrollTo({ top: container.scrollHeight, behavior: "auto" });
            }
          });
        }
      });
    };

    if (isWelcome || !pendingAskMessage?.meta?.askUserQuestion) {
      setAskDockExtraSpace(0);
      return () => {
        if (frame) window.cancelAnimationFrame(frame);
        setAskDockExtraSpace(0);
      };
    }

    scheduleLayoutUpdate({ scroll: true });
    let resizeObserver: ResizeObserver | null = null;
    if ("ResizeObserver" in window) {
      resizeObserver = new ResizeObserver(() => scheduleLayoutUpdate({ scroll: true }));
      const dock = askDockRef.current;
      const card = dock?.querySelector<HTMLElement>('[data-ask-user-question-card][data-ask-status="pending"]');
      const composer = document.getElementById("composer");
      if (dock) resizeObserver.observe(dock);
      if (card) resizeObserver.observe(card);
      if (composer) resizeObserver.observe(composer);
    }
    const handleResize = () => scheduleLayoutUpdate({ scroll: false });
    window.addEventListener("resize", handleResize);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", handleResize);
      setAskDockExtraSpace(0);
    };
  }, [isWelcome, pendingAskMessage?.id, pendingAskMessage?.meta?.askUserQuestion]);

  return {
    threadRef,
    askDockRef,
    threadFollowPausedRef,
    lastThreadScrollTopRef,
    setRunningActive,
    threadScrollContainer,
    isThreadNearBottom,
    scrollThreadToBottom,
    resumeThreadFollow,
    scheduleThreadFollow
  };
}
